import { CONVERSIONS } from '../constants';

/**
 * Formats area measurement in both metric and imperial units.
 *
 * Automatically chooses appropriate units based on size:
 * - Metric: m² for small areas, km² for large areas (≥1,000,000 m²)
 * - Imperial: ft² for small areas, mi² for large areas (≥27,878,400 ft²)
 *
 * @param areaInSquareMeters - Area value in square meters
 * @returns Formatted string with both metric and imperial units
 *
 * @example
 * formatArea(1500);        // "1,500 m² / 16,146 ft²"
 * formatArea(2000000);     // "2.00 km² / 0.77 mi²"
 */
export const formatArea = (areaInSquareMeters: number): string => {
  let metricText = '';
  if (areaInSquareMeters >= CONVERSIONS.SQUARE_METERS_PER_SQUARE_KILOMETER) {
    const squareKilometers = (
      areaInSquareMeters / CONVERSIONS.SQUARE_METERS_PER_SQUARE_KILOMETER
    ).toFixed(2);
    metricText = `${squareKilometers} km²`;
  } else {
    const squareMeters = Math.round(areaInSquareMeters);
    metricText = `${squareMeters} m²`;
  }

  const areaInSquareFeet =
    areaInSquareMeters * CONVERSIONS.SQUARE_METERS_TO_SQUARE_FEET;
  let imperialText = '';

  if (areaInSquareFeet >= CONVERSIONS.SQUARE_FEET_PER_SQUARE_MILE) {
    const squareMiles = (
      areaInSquareFeet / CONVERSIONS.SQUARE_FEET_PER_SQUARE_MILE
    ).toFixed(2);
    imperialText = `${squareMiles} mi²`;
  } else {
    const squareFeet = Math.round(areaInSquareFeet);
    imperialText = `${squareFeet.toLocaleString()} ft²`;
  }

  return `${metricText} / ${imperialText}`;
};
